import { useEffect, useState } from "react";
import ReactMarkdown from "react-markdown";
import api from "@/lib/api";
import { Search, FileText, Wrench, FileArchive, Play, ClipboardCopy, Download } from "lucide-react";
import { downloadDocument, prettySize } from "@/lib/download";

const CATS = [
  { key: "all",       label: "All" },
  { key: "script",    label: "Scripts",   Icon: ClipboardCopy },
  { key: "template",  label: "Templates", Icon: FileText },
  { key: "pdf",       label: "PDFs",      Icon: FileArchive },
  { key: "tool",      label: "Tools",     Icon: Wrench },
  { key: "video",     label: "Videos",    Icon: Play },
];

const STAGES = ["all", "methodology", "market", "product", "admin", "metrics"];

export default function Library() {
  const [q, setQ] = useState("");
  const [cat, setCat] = useState("all");
  const [stage, setStage] = useState("all");
  const [items, setItems] = useState([]);
  const [docs, setDocs] = useState([]);

  const load = () => {
    const params = {};
    if (q) params.q = q;
    if (cat !== "all") params.category = cat;
    if (stage !== "all") params.stage = stage;
    api.get("/resources", { params }).then((r) => setItems(r.data));
    const docParams = {};
    if (stage !== "all") docParams.stage = stage;
    api.get("/documents", { params: docParams }).then((r) => setDocs(r.data)).catch(() => setDocs([]));
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, [cat, stage]);

  return (
    <div className="space-y-10">
      <div className="grid lg:grid-cols-12 gap-6 items-end">
        <div className="lg:col-span-8">
          <div className="label-eyebrow mb-3">Resource Library</div>
          <h1 className="font-display text-5xl md:text-6xl leading-[1.02]">Every asset. One shelf.</h1>
          <p className="mt-4 text-neutral-500 max-w-xl">Scripts, templates, PDFs, videos, tools — the entire operating stack for a new hire.</p>
        </div>
        <div className="lg:col-span-4">
          <div className="relative">
            <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-neutral-400"/>
            <input
              placeholder="Search collateral…" value={q}
              onChange={(e)=>setQ(e.target.value)}
              onKeyDown={(e)=> e.key === "Enter" && load()}
              data-testid="library-search"
              className="w-full pl-10 pr-4 py-3 bg-transparent border hairline focus:outline-none focus:border-[color:var(--brand-primary)] focus:ring-1 focus:ring-[color:var(--brand-primary)]"
            />
          </div>
        </div>
      </div>

      <div className="flex flex-wrap gap-2">
        {CATS.map(c => (
          <button
            key={c.key}
            onClick={()=>setCat(c.key)}
            data-testid={`filter-cat-${c.key}`}
            className={`px-4 py-2 text-xs uppercase tracking-widest border hairline transition-colors ${cat===c.key?"bg-[color:var(--brand-primary)] text-white border-[color:var(--brand-primary)]":"hover:bg-[color:var(--brand-surface2)]"}`}
          >{c.label}</button>
        ))}
      </div>

      <div className="flex flex-wrap gap-2">
        {STAGES.map(s => (
          <button
            key={s}
            onClick={()=>setStage(s)}
            data-testid={`filter-stage-${s}`}
            className={`px-3 py-1.5 text-xs uppercase tracking-widest transition-colors ${stage===s?"text-[var(--brand-primary)] border-b border-[color:var(--brand-primary)]":"text-neutral-500 hover:text-black"}`}
          >{s}</button>
        ))}
      </div>

      {cat === "all" && docs.length > 0 && (
        <div data-testid="library-documents">
          <div className="label-eyebrow mb-3">Documents</div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
            {docs.map(d => (
              <button
                key={d.id}
                onClick={()=>downloadDocument(d)}
                data-testid={`library-doc-${d.id}`}
                className="card-hairline p-5 flex items-center gap-3 text-left"
              >
                <FileText size={20} className="text-[var(--brand-primary)] shrink-0" />
                <div className="flex-1 min-w-0">
                  <div className="font-medium truncate">{d.title}</div>
                  <div className="text-xs text-neutral-500 font-mono truncate">{d.original_filename} · {prettySize(d.size)}{d.stage?` · ${d.stage}`:""}</div>
                </div>
                <Download size={15} className="text-neutral-400 shrink-0" />
              </button>
            ))}
          </div>
        </div>
      )}

      <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
        {items.map((r) => (
          <div key={r.id} className="card-hairline p-6 flex flex-col" data-testid={`resource-${r.id}`}>
            <div className="flex items-center justify-between">
              <div className="label-eyebrow text-[var(--brand-primary)]">{r.category}</div>
              <div className="label-eyebrow text-neutral-400">{r.stage || "—"}</div>
            </div>
            <div className="mt-6 font-display text-xl leading-tight">{r.title}</div>
            <div className="mt-2 text-sm text-neutral-500 flex-1">{r.description}</div>
            {r.body && (
              <details className="mt-4 text-sm">
                <summary className="cursor-pointer text-[var(--brand-primary)] link-underline inline-block">View content</summary>
                <div className="mt-3 prose-editorial bg-[color:var(--brand-surface2)] p-4 text-sm">
                  <ReactMarkdown>{r.body}</ReactMarkdown>
                </div>
              </details>
            )}
            {r.url && (
              <a href={r.url} target="_blank" rel="noopener noreferrer" className="mt-3 text-sm text-[var(--brand-primary)] link-underline">Open file →</a>
            )}
            <div className="mt-4 flex flex-wrap gap-1.5">
              {(r.tags||[]).map(t => <span key={t} className="text-[10px] uppercase tracking-widest text-neutral-500 border hairline px-2 py-0.5">{t}</span>)}
            </div>
          </div>
        ))}
        {items.length === 0 && <div className="text-sm text-neutral-500">No resources match your filters.</div>}
      </div>
    </div>
  );
}
